import Random from "~/ts/library/Random";
import {Reactive, reactive} from "vue";
import AbstractApiRequest from "~/core-ui/ts/request/AbstractApiRequest";
import {AbstractApiResult} from "~/core-ui/ts/request/AbstractApiResult";

interface StateInterface<T> {
    loaded: boolean,
    loading: boolean,
    data?: T,
    code?: number,
    descr?: string,
    loadTimestamp?: number
}

export default class ReactiveRequest<T extends object> {
    protected _request: AbstractApiRequest<any, T>;

    private _uid: string | undefined = undefined;
    private callback?: (data: T) => void;
    private errorCallback?: (result?: AbstractApiResult<any, any>) => void;
    private showErrorMessage: boolean = true;
    private state: Reactive<StateInterface<T>>;
    private makePromise?: Promise<this>

    public constructor(request: AbstractApiRequest<any, T>, autoStart: boolean = true) {
        this.state = reactive({
            loaded: false,
            loading: false,
            data: undefined,
            code: undefined,
            descr: undefined,
            loadTimestamp: undefined
        });
        this.reset();
        this._request = request;
        if (typeof this.requestConfig.silent != "boolean") {
            this.requestConfig.silent = true;
        }

        if (autoStart) {
            this.make();
        }
    }

    get uid(): string | undefined {
        return this._uid;
    }

    get code(): number | undefined {
        return this.state.code;
    }

    set code(value: number | undefined) {
        this.state.code = value;
    }

    get descr(): string | undefined {
        return this.state.descr;
    }

    set descr(value: string | undefined) {
        this.state.descr = value;
    }

    get data(): T {
        return this.state.data as any;
    }

    set data(data: T) {
        this.state.data = data as any;
    }

    get loading(): boolean {
        return this.state.loading;
    }

    get loaded(): boolean {
        return this.state.loaded;
    }

    public setShowErrorMessage(value: boolean) {
        this.showErrorMessage = value;
        return this;
    }

    get requestConfig() {
        return this._request.options;
    }

    setCallback(callback?: (data: T) => void) {
        this.callback = callback;
        return this;
    }

    setErrorCallback(callback?: (result?: AbstractApiResult<any, any>) => void) {
        this.errorCallback = callback;
        return this;
    }

    private reset() {
        this.code = undefined;
        this.descr = undefined;
        this.state.data = undefined;
    }

    public get isLoading() {
        return this.state.loading;
    }

    public get isLoaded(): boolean {
        return this.state.loaded;
    }

    get isSuccess() {
        return this.code == 1;
    }

    getRequestParam(key: string) {
        return this.requestConfig.p ? this.requestConfig.p[key] : null;
    }

    addRequestParam(key: string, value: any) {
        if (!this.requestConfig.p) {
            this.requestConfig.p = {};
        }
        this.requestConfig.p[key] = value;
        //this.requestConfig.p[key] = value;
        return this;
    }

    protected processData(data: T) {

    }

    public make() {
        this.makePromise = new Promise<this>(resolve => {
            this.reset();
            let uid = this._uid = Random.uid();

            this.state.loaded = false;
            this.state.loading = true;
            setTimeout(async () => {

                try {
                    let result = await this._request.send();//new LcabApiRequest<T>(this.requestConfig, this.resultType)).send();
                    if (this._uid == uid) {
                        if (result.isSuccess) {
                            this.data = result.getData();
                            this.processData(this.data);
                            if (this.callback) {
                                try {
                                    this.callback(this.data);
                                } catch (e) {
                                    console.error(e);
                                }
                            }
                        } else {
                            if (this.showErrorMessage) {
                                result.showMessageOnError();
                            }
                            if (this.errorCallback) {
                                this.errorCallback(result);
                            }
                        }
                        this.code = result.code;
                        this.descr = result.descr;
                    }
                } catch (e) {
                    if (this._uid == uid && this.errorCallback) {
                        this.errorCallback();
                    }
                }
                if (this._uid == uid) {
                    this.state.loaded = true;
                    this.state.loading = false;
                    this.state.loadTimestamp = Date.now();
                }
                resolve(this);
            }, 0);
        });
        return this.makePromise;
    }

    public getCurrentMakePromise(): Promise<ReactiveRequest<T>> | undefined {
        return this.makePromise;
    }

    get loadTimestamp(): number | undefined {
        return this.state.loadTimestamp;
    }

}
